import {
  AwsWellStatus,
  EvaSource,
  RigFracActivityMetric,
  RigFracGroupBy,
  RigFracTimeScale,
  TimeStep,
  WellStatus,
  WellStatusTimeScale
} from "constants/charts.enums";
import { ColorLockedLegendItem, IUserForecastSettings } from "store/features";

import { TypeWellId } from "api/userForecasts";

import { GroupBy, INormalizeBySetting, PerformanceDateRange } from "models";
import { mBinSize } from "models/binSize";
import { ColorPaletteSpecialColors, PreferredColor } from "models/colorPalette";
import { EntityKind } from "models/entityKind";
import { RegressionType } from "models/regressionType";
import { ForecastTrumpParamsType } from "models/schemas/data.schema";

import { ColorScale, PaletteType } from "components/user-settings/models/IColorPalette";

import { BoundingBox } from "./base";
import { TypeWellSeries } from "./typeWellSeries";

export type ColorPaletteSetting = {
  id: string;
  name: string;
  colorScale: ColorScale[];
  reverse: boolean;
  shared: boolean;
  paletteType: PaletteType;
  thickness: number[];
  colors: string[];
  // Truthful IColorPalette shapes (EVA-76 review): a live user palette carries
  // OBJECT entries here, not strings — the previous string[] declarations were
  // never what the runtime forwarded, and chart-svc's rigFracChart contract
  // (RigFracColorPalette) now binds these exact object shapes.
  preferredColors: PreferredColor[];
  specialColors: Nullable<ColorPaletteSpecialColors>;
  // Rig/frac charts on AWS: set when the user picked this palette, so an
  // explicit Plasma pick is not mistaken for the default (EVA-338).
  userPicked?: boolean;
};

type UserForecast = {
  folderId: string;
  id: string;
  type: "folder" | "forecast";
};

type ChartVariable = {
  valueProperty: string;
  pdenSource?: string;
  displayTitle: string;
  canBin: boolean;
  dataType: string;
  mappingName: string;
  bin?: Nullable<mBinSize>;
};

type ChartSettingBase = {
  product: string;
  chartType: number; // could be enum
  survivorBias: boolean;
  rateType: number; // could be enum
  cutoff: number;
  timeStep: TimeStep;
  shutInMonths: number;
  movingAverageDays?: number;
  source: EvaSource;
  normalizeBy: INormalizeBySetting; // check this
  useWeightedRatioAverage: boolean;
  xVariable?: ChartVariable;
  yVariable?: ChartVariable;
  lockUnits: boolean;
  showForecast: boolean;
  pdenDataSourceSetting: {
    source: EvaSource;
    timeStep: TimeStep;
    /** Per-chart override: restrict production data to one specific public source.
     *  Uses the DataSource proto enum's numeric value (e.g. gDC=0, McDan=4, Novi=5).
     *  Omit (or set undefined) to use all public sources — backward compatible.
     *  IMPORTANT: gDC == 0, so always check `!= null`, never truthiness. */
    specificPublicSource?: number;
  };
  forecastMode: string;
  useForecastStartDate: boolean;
};

type ChartFieldInformation = {
  xField: ChartField;
  yField: ChartField;
  groupByField: ChartField;
};

type LineOfBestFitInformation = {
  regressionType: RegressionType;
  color: string;
  thickness: number;
  forceRegressionThroughOrigin: boolean;
};

interface BaseLineChartSettings extends ChartSettingBase {
  otherProducts: string[];
  primaryProducts?: string[];
  showWellCount: boolean;
  showIndividualSeries: boolean;
  averageSettings: { color: string; thickness: number };
  showAverage: boolean;
  p10Settings: { color: string; thickness: number };
  showP10: boolean;
  p50Settings: { color: string; thickness: number };
  showP50: boolean;
  p90Settings: { color: string; thickness: number };
  showP90: boolean;
  combineSameUnitAxis?: boolean;
}
export interface RateCumChartSettings extends BaseLineChartSettings {
  pressureChartSettings: { product: string; productivityIndex: boolean };
  totalFluidChartSettings?: { product: string };
}
export type CumTimeChartSettings = BaseLineChartSettings;
export interface RateTimeChartSettings extends BaseLineChartSettings {
  pressureChartSettings: { product: string; productivityIndex: boolean };
}
export interface RecoveryPVIChartSettings extends BaseLineChartSettings {
  pressureChartSettings: { product: string; productivityIndex: boolean };
  recoveryFactorFields: string[];
}

export interface RecoveryTimeChartSettings extends BaseLineChartSettings {
  pressureChartSettings: { product: string; productivityIndex: boolean };
  recoveryFactorFields: string[];
}

export interface RateDateChartSettings extends BaseLineChartSettings {
  annotationSettings: Partial<{
    showAnnotations: boolean;
  }>;
}

export type TotalRateDateChartSettings = BaseLineChartSettings;
export interface TotalRateCumChartSettings extends BaseLineChartSettings {
  totalFluidChartSettings?: { product: string };
}
export type DeclineRateChartSettings = BaseLineChartSettings;
export interface WellContributionChartSettings extends BaseLineChartSettings {
  showLicensedVolumeLine?: boolean;
  useChartFocus?: boolean;
}
export interface TrendDateChartSettings extends BaseLineChartSettings {
  stackedBarCalculation: number;
}

// Injector Total: X is always the monthly On Inj Date bucket, Y is always Well Count
// (stackedBarCalculation mirrors StackedBar/TrendDate's use of the field for the calculation
// method — CalculationMethod.Count here). Neither axis is user-editable.
export interface InjectorTotalChartSettings extends BaseLineChartSettings {
  stackedBarCalculation: number;
}

export type CAGRChartSettings = BaseLineChartSettings;
export type PieChartSettings = ChartSettingBase;
export type MosaicChartSettings = ChartSettingBase;
export interface MaterialBalanceTimeChartSettings extends ChartSettingBase {
  showIndividualSeries: boolean;
  showAverage: boolean;
  averageSettings: { color: string; thickness: number };
}
export interface ProbitChartSettings extends ChartSettingBase {
  showLinesOfBestFit: boolean;
  showZeros: boolean;
}

export interface StackedBarChartSettings extends ChartSettingBase {
  product: string; // could be enum
  otherProducts: string[];
  showWellCount: boolean;
  averageSettings: { color: string; thickness: number };
  showAverage: boolean;
  p10Settings: { color: string; thickness: number };
  showP10: boolean;
  p50Settings: { color: string; thickness: number };
  showP50: boolean;
  p90Settings: { color: string; thickness: number };
  showP90: boolean;
  showIndividualSeries: boolean;
  stackedBarCalculation: number;
  pressureChartSettings: { product: string; productivityIndex: boolean };
  annotationSettings: Partial<{
    showAnnotations: boolean;
  }>;
  normalizeBy: INormalizeBySetting;
  splitYearActive: boolean;
}

export interface BoxPlotChartSettings extends ChartSettingBase {
  product: string; // could be enum
  otherProducts: string[];
  showWellCount: boolean;
  averageSettings: { color: string; thickness: number };
  showAverage: boolean;
  p10Settings: { color: string; thickness: number };
  showP10: boolean;
  p50Settings: { color: string; thickness: number };
  showP50: boolean;
  p90Settings: { color: string; thickness: number };
  showP90: boolean;
  showIndividualSeries: boolean;
  stackedBarCalculation: number;
  pressureChartSettings: { product: string; productivityIndex: boolean };
  annotationSettings: Partial<{
    showAnnotations: boolean;
  }>;
  normalizeBy: INormalizeBySetting;
}

type BoundingBoxMap = Partial<Record<BoundingBox, Maybe<number>>>;

export interface ChartRequestType {
  FilterId: string;
  requestId: string;
  ReverseColor: boolean;
  ColorPalette: ColorPaletteSetting;
  colorLockedItems: Record<string, ColorLockedLegendItem>;
  GroupBy: GroupBy;
  sortBy: string;
  entityKind: EntityKind;
  overrideSizeLimit?: boolean; // responsible for surpassing size limit
  showGroupsNotInFilter: boolean;
  placeholderBinsEnabled: boolean;
  hasCheckedBins: boolean;
  normalizeBySetting: INormalizeBySetting;
  userForecast: UserForecast[];
  userForecastSettings: IUserForecastSettings;
  /**
   * Forecast Trumping settings for trumping chart AXES (EVA-199) — the same object the focus,
   * legend, map and data table send to the data service, so every surface trumps from one
   * definition. Group-by trumping stays on GroupBy.forecastTrumpingSettings.
   */
  forecastTrumpingSettings?: ForecastTrumpParamsType;
  forecastMode: string;
  useForecastStartDate: boolean;
  syncClientPden: boolean;
  syncClientSchema: string;
  syncClientFcst: boolean;
  syncForecastRescat: string;
  chartSetting: ChartSettingBase;
  axisMinMax: BoundingBoxMap;
  isDefaultLockOn: boolean;
  TypeWellSeriesArpsWasm?: TypeWellSeries[];
}

export interface LineChartRequestType extends ChartRequestType {
  showAverage?: boolean;
  showP10?: boolean;
  showP50?: boolean;
  showP90?: boolean;
  typeWells: string[];
  showIndividualSeries?: boolean;
  normalizeTypeWell?: boolean;
  combineSameUnitAxis?: boolean;
}

export interface BoxPlotChartRequestType extends ChartRequestType {
  showScatter: boolean;
  normalizeTypeWell: boolean;
  chartSetting: BoxPlotChartSettings;
  chartFieldInformation: ChartFieldInformation;
}

type ChartField = {
  type: "x" | "y" | "groupBy";
  folderName?: string;
  isForecast?: boolean;
  product?: string;
  defaultUnit?: string;
  title: string;
  property: string;
  pdenSource?: number;
  performanceDateRange?: PerformanceDateRange;
};

export interface CrossPlotChartRequestType extends ChartRequestType {
  chartFieldInformation: ChartFieldInformation;
  typeWells: string[] | TypeWellId[];
  normalizeTypeWell: boolean;
  showCrossPlotLineOfBestFit: boolean;
  lineOfBestFitInformation: LineOfBestFitInformation;
}

export interface ProbitChartRequestType extends ChartRequestType {
  chartFieldInformation: ChartFieldInformation;
  chartSetting: ProbitChartSettings;
  useProbitDataFromLineOfBestFit: boolean;
}

export interface RateCumChartRequestType extends LineChartRequestType {
  chartSetting: RateCumChartSettings;
}

export interface RecoveryPVIChartRequestType extends LineChartRequestType {
  chartSetting: RecoveryPVIChartSettings;
}

export interface RecoveryTimeChartRequestType extends LineChartRequestType {
  chartSetting: RecoveryTimeChartSettings;
}

export interface CumTimeChartRequestType extends LineChartRequestType {
  chartSetting: CumTimeChartSettings;
}

export interface RateTimeChartRequestType extends LineChartRequestType {
  chartSetting: RateTimeChartSettings;
}
export interface PieChartRequestType extends ChartRequestType {
  chartSetting: PieChartSettings;
}
export interface RateDateChartRequestType extends LineChartRequestType {
  chartSettings: RateDateChartSettings;
}
export interface TotalRateCumChartRequestType extends LineChartRequestType {
  chartSetting: TotalRateCumChartSettings;
}
export interface CAGRChartRequestType extends LineChartRequestType {
  chartSetting: CAGRChartSettings;
}

export interface DeclineRateChartRequestType extends LineChartRequestType {
  chartSetting: DeclineRateChartSettings;
}

export interface WellContributionChartRequestType extends LineChartRequestType {
  chartSetting: WellContributionChartSettings;
  chartFieldInformation: ChartFieldInformation;
  // The well filter id. Well-contribution charts are facility-kind, so FilterId is the facility
  // filter; the backend needs the well filter to build the upstream group-by that drives colours.
  WellFilterId?: string;
}
export interface TotalRateDateChartRequestType extends ChartRequestType {
  chartSetting: ChartSettingBase & {
    otherProducts: string[];
    showWellCount: boolean;
  };
}

export interface TrendDateChartRequestType extends LineChartRequestType {
  chartSetting: TrendDateChartSettings;
  chartFieldInformation: ChartFieldInformation;
}

export interface InjectorTotalChartRequestType extends LineChartRequestType {
  chartSetting: InjectorTotalChartSettings;
  chartFieldInformation: ChartFieldInformation;
}

export interface MosaicChartRequestType extends ChartRequestType {
  chartSetting: MosaicChartSettings;
  chartFieldInformation: ChartFieldInformation;
}

export interface StackedBarChartRequestType extends ChartRequestType {
  chartSetting: StackedBarChartSettings;
  chartFieldInformation: ChartFieldInformation;
}

export interface MaterialBalanceTimeRequestType extends ChartRequestType {
  chartSetting: MaterialBalanceTimeChartSettings;
}

// --- Rig/Frac Charts widget (EVA-76) ---------------------------------------
// Lean request for POST api/v1/rigFracChart. Deliberately does NOT extend
// ChartRequestType: the rig/frac chart carries none of the wells/facilities
// payload (FilterId, GroupBy, forecast, normalization, palette, …).

// This set must stay in lockstep with eva-chart-svc's
// RigFracActivityFilters (RigFracActivityRequest.cs) and eva-data-svc-rs's
// RigFracFilters (models/rigfrac_activity.rs), which is a superset (also
// `type`). A field added here without adding it there silently drops at
// chart-svc.
export interface RigFracActivityFilters {
  operator?: string[];
  county?: string[]; // countyUnique form, e.g. "Eddy (NM)"
  basin?: string[];
  subBasin?: string[];
  locationType?: string[];
  stateProvince?: string[];
  country?: string[];
}

export interface RigFracActivityBbox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

export interface RigFracActivityCustomRange {
  from: string; // ISO YYYY-MM-DD
  to: string; // ISO YYYY-MM-DD
}

// One main-focus bin: the label the focus list shows, and every normalized
// spelling (raw, rig/frac, company names) that counts toward it.
export type RigFracFocusBin = { label: string; keys: string[] };

// EVA-149. primaryMetric (the stacked bars) and Recent Activity's single
// `metric` are counts only — a price series has no per-group breakdown to
// stack/rank, and `None` means "no secondary axis", which only makes sense on
// the secondary (overlay line) field. chart-svc rejects either at runtime
// with a 400 (FR-016); this narrowing makes the same mistake fail to compile
// instead. secondaryMetric keeps the FULL union — it's the only field that
// can carry a price or None.
export type RigFracActivityOnlyMetric = Exclude<
  RigFracActivityMetric,
  | RigFracActivityMetric.WtiPrice
  | RigFracActivityMetric.HenryHubPrice
  | RigFracActivityMetric.None
>;

// Type alias (not interface) so the implicit index signature satisfies
// createMergeWithDefaults' GenericObject constraint in the request factory.
export type RigFracActivityChartRequestType = {
  chartType: "RigFracActivity";
  primaryMetric: RigFracActivityOnlyMetric;
  secondaryMetric: RigFracActivityMetric;
  groupBy: RigFracGroupBy;
  timeScale: RigFracTimeScale;
  // Required iff timeScale === Custom.
  customRange: Nullable<RigFracActivityCustomRange>;
  filters: RigFracActivityFilters;
  // Optional per-layer selections (siblings of the flat `filters` fallback
  // above). Deliberately not added to the entity defaults in
  // entities/charts/eva/requests/rigFracActivity.ts — the controller assigns
  // these after createEntityState, never as overrides.
  rigFilters?: RigFracActivityFilters;
  fracFilters?: RigFracActivityFilters;
  // EVA-105. Main well filter; sent on AWS only while a permits metric is selected.
  filterId?: string;
  // The main focus's bins, sent while this chart is grouped by the focus field;
  // foldOthers only when the focus has an Others bin. Assigned after
  // createEntityState, never defaults: a default would persist a stale list.
  focusBins?: RigFracFocusBin[];
  foldOthers?: boolean;
  // The chart's own bin override: top N groups by the chart's value, the rest
  // folded into Others. null means the override is on with no cap (every group
  // shows); absent means the chart follows the focus bins.
  maxBins?: Nullable<number>;
  bbox: Nullable<RigFracActivityBbox>;
  // Server-side bar palette; defaults to the user's active palette in the controller.
  colorPalette: Nullable<ColorPaletteSetting>;
  // EVA-76 wire names for primaryMetric/secondaryMetric, superseded by EVA-149.
  // Present only on documents saved before the rename — never written by new
  // code — so reads of a stored dashboard can still fall back to them.
  /** @deprecated EVA-76 name for `primaryMetric`. Stored documents only. */
  leftMetric?: RigFracActivityOnlyMetric;
  /** @deprecated EVA-76 name for `secondaryMetric`. Stored documents only. */
  rightMetric?: RigFracActivityMetric;
};

// Recent Activity variant (EVA-76): a SINGLE `metric` (not left/right) and no
// time-scale/customRange. Same endpoint, discriminated by `chartType`.
export type RigFracRecentActivityChartRequestType = {
  chartType: "RigFracRecentActivity";
  metric: RigFracActivityOnlyMetric;
  groupBy: RigFracGroupBy;
  filters: RigFracActivityFilters;
  // See the matching comment on RigFracActivityChartRequestType.
  rigFilters?: RigFracActivityFilters;
  fracFilters?: RigFracActivityFilters;
  filterId?: string;
  focusBins?: RigFracFocusBin[];
  foldOthers?: boolean;
  maxBins?: Nullable<number>;
  bbox: Nullable<RigFracActivityBbox>;
  colorPalette: Nullable<ColorPaletteSetting>;
};

// Discriminated union on `chartType` for the shared POST/export/controller path.
export type RigFracChartRequestType =
  | RigFracActivityChartRequestType
  | RigFracRecentActivityChartRequestType;

// Request-side chartType discriminators (`request.chartType`). These are DISTINCT
// from the options-side `EvaChart` enum values (`options.chartType`, e.g.
// `EvaChart.RigFracActivity === "Activity vs Time"`): the strings here match the
// request body the controller POSTs. Kept beside the request types — and tied to
// them via `satisfies` so a discriminant rename fails to compile — so screenshot
// hooks reference these instead of hardcoding the literals (CLAUDE.md: no
// hardcoded chart-kind strings).
export const RIG_FRAC_REQUEST_CHART_TYPES = {
  activity: "RigFracActivity",
  recentActivity: "RigFracRecentActivity"
} as const satisfies {
  activity: RigFracActivityChartRequestType["chartType"];
  recentActivity: RigFracRecentActivityChartRequestType["chartType"];
};

export const isRigFracActivityRequestChartType = (chartType?: Maybe<string>): boolean =>
  chartType === RIG_FRAC_REQUEST_CHART_TYPES.activity;

export const isRigFracRecentActivityRequestChartType = (
  chartType?: Maybe<string>
): boolean => chartType === RIG_FRAC_REQUEST_CHART_TYPES.recentActivity;

// EVA-149 (T037) compile-time proof of the RigFracActivityOnlyMetric narrowing
// above lives in requests.test-d.ts, not here — this is a production module,
// not a place for runtime-dead assertion functions.

// --- Well Status widget --------------------------------------------
// Lean request for POST api/v1/wellStatusChart. Like the rig/frac requests it
// deliberately does NOT extend ChartRequestType (no products, forecast,
// normalization or decline payload) — but unlike them these ARE wells charts, so
// they carry `filterId` and a focus grouping. There is no bbox: the charts follow
// the dashboard filter only, never the map viewport.

export interface WellStatusCustomRange {
  from: string; // ISO YYYY-MM-DD
  to: string; // ISO YYYY-MM-DD
}

/**
 * Grouping + colouring for the widget's charts.
 *
 * `seriesColors` and `colorPalette` are alternatives, one per colour mode:
 * - focus-synced (default): the UI knows the focus bins, so it resolves
 *   label → hex from the legend and sends `seriesColors`;
 * - Chart Focus override: the override property may be binned server-side, so the
 *   UI cannot know the labels yet and sends `colorPalette` for the server to
 *   spread over the returned bins (the rig/frac colouring path).
 * The server prefers `seriesColors` when both are present.
 */
export interface WellStatusFocus {
  property: string;
  /**
   * The focus's DISPLAY name ("Resource Play") for the raw `property`
   * ("Header.ResourcePlay"). Sent so the EXPORTED table can head its first column
   * the way the on-screen one does — the name lives on IGroupBy, so chart-svc has
   * no way to derive it. Not used for anything the chart itself renders.
   */
  title?: string;
  bin?: Nullable<mBinSize>;
  seriesColors?: Record<string, string>;
  colorPalette?: Nullable<ColorPaletteSetting>;
}

// Type alias (not interface) so the implicit index signature satisfies
// createMergeWithDefaults' GenericObject constraint in the request factory.
export type WellStatusOverTimeChartRequestType = {
  chartType: "WellStatusOverTime";
  /** An Azure WellStatus label ("DUC") or, on AWS, an Insights label ("Permit Approved"). */
  status: WellStatus | AwsWellStatus;
  timeScale: WellStatusTimeScale;
  // Required iff timeScale === Custom.
  customRange: Nullable<WellStatusCustomRange>;
  focus: WellStatusFocus;
  filterId: Nullable<string>;
  /** Count multilateral drain legs (their post-rig-release dates come from the parent well). */
  includeDrainLegs: boolean;
};

// Current-status variant: no time axis, and `status` is a verbatim
// `header.status` value from the data rather than a derived WellStatus.
export type WellStatusCurrentChartRequestType = {
  chartType: "WellStatusCurrent";
  /** Verbatim `header.status`. Ignored when `allStatuses` is true. */
  status: string;
  /**
   * Break every status out as its own stack segment instead of filtering to one.
   * An explicit flag rather than a magic `status` value: that field carries
   * verbatim `header.status` strings, a namespace the DATA owns, so an in-band
   * sentinel could collide with a status an org actually holds.
   */
  allStatuses: boolean;
  focus: WellStatusFocus;
  filterId: Nullable<string>;
  includeDrainLegs: boolean;
};

// Discriminated union on `chartType` for the shared POST/export/controller path.
export type WellStatusChartRequestType =
  | WellStatusOverTimeChartRequestType
  | WellStatusCurrentChartRequestType;

// Request-side chartType discriminators — same convention (and same reason) as
// RIG_FRAC_REQUEST_CHART_TYPES: distinct from the options-side `EvaChart` values,
// and `satisfies`-tied so a rename fails to compile.
export const WELL_STATUS_REQUEST_CHART_TYPES = {
  overTime: "WellStatusOverTime",
  current: "WellStatusCurrent"
} as const satisfies {
  overTime: WellStatusOverTimeChartRequestType["chartType"];
  current: WellStatusCurrentChartRequestType["chartType"];
};

export const isWellStatusOverTimeRequestChartType = (
  chartType?: Maybe<string>
): boolean => chartType === WELL_STATUS_REQUEST_CHART_TYPES.overTime;

export const isWellStatusCurrentRequestChartType = (chartType?: Maybe<string>): boolean =>
  chartType === WELL_STATUS_REQUEST_CHART_TYPES.current;

/**
 * Whether a chart request is the Current-status chart in All Statuses mode.
 *
 * Consumers read `request` off the chart entity, which is typed as the union of
 * every chart's request, so reaching `allStatuses` needs a narrowing step. This is
 * that step, in one place: the alternative — and what several call sites were
 * doing — is an inline `as unknown as { allStatuses?: boolean }`, which re-declares
 * the field's shape from scratch and would go on compiling if the real one were
 * renamed or retyped.
 *
 * Checks `chartType` too. `allStatuses` belongs to this request alone, so a truthy
 * flag on any other chart's request is a bug, not a mode.
 */
export const isAllStatusesRequest = (request?: Maybe<unknown>): boolean => {
  const candidate = request as Maybe<Partial<WellStatusCurrentChartRequestType>>;
  return (
    isWellStatusCurrentRequestChartType(candidate?.chartType) &&
    candidate?.allStatuses === true
  );
};
